// @flow
import React, { type ElementType, type Node, Fragment, useContext, useRef } from 'react';

import { CriteriaContext } from 'Components/widgets/criteria/CriteriaContext';
import { ReactPageContext } from 'Context/pageContext';
import { ID_LAYER_HEADER__SHOPPING__CRITERIA_TRIGGER } from 'Components/utils/identifiers/constants';
import { ICONS } from 'Components/utils/icon/icons';
import { COLORS } from 'Components/utils/color/colors';
import ResponsiveContext from 'Components/utils/responsive/responsiveContext';
import { generateUniqueId } from 'Utils/unique-id/uniqueId';
import { useTracking } from 'Tracking/tracking-api/useTracking';
import { t } from 'Src/i18n';

import { Button } from 'Components/frameworks/button/Button';
import type { ButtonProps } from 'Components/frameworks/button/Button';
import { DropDown } from 'Components/frameworks/drop-down/DropDown.jsx';
import {
  InputButton,
  type InputButtonProps,
} from 'Components/frameworks/form-inputs/input-button/InputButton.jsx';
import { Layer } from 'Components/frameworks/layer/Layer';

type Props = {
  children: Node,
  buttonIcon?: $PropertyType<InputButtonProps, 'iconName'>,
  buttonLabel?: Node,
  buttonIsFilled?: $PropertyType<InputButtonProps, 'isFilled'>,
  dropDownMinWidth?: number,
  isLoading?: boolean,
  isOpen?: boolean,
  onOpen?: () => void,
  onClear?: () => void,
  onClose: () => void,
  trackingLabel?: string,
  buttons: Array<ButtonProps>,
  reassurance: {
    shouldDisplay: boolean,
    text: string,
  },
};

export const CriteriaTrigger = (props: Props) => {
  const { isDesktop } = useContext(ResponsiveContext);
  const [criteriaContext] = useContext(CriteriaContext);
  const pageContext = useContext(ReactPageContext);
  const rootRef: { current: ?React$ElementRef<ElementType> } = useRef();
  const track = useTracking();

  const buttons = props.buttons.map(btnProps => {
    const { children, ...allOtherProps } = btnProps;
    return (
      <div
        key={generateUniqueId(
          [btnProps.theme, btnProps.color, btnProps.colorBg, btnProps.colorBorder],
          'criteria-btn-',
        )}
      >
        <Button {...allOtherProps}>{children}</Button>
      </div>
    );
  });

  const criteriaDropdownOrLayer = isDesktop ? (
    <div className="flex flex-2">
      <DropDown
        className="flex-1 mx-2"
        contentStyle={{ minWidth: props.dropDownMinWidth || 300 }}
        isExpanded={props.isOpen}
        onClose={props.onClose}
        rootRef={rootRef}
        theme="hollow"
      >
        {() => (
          <Fragment>
            {props.children}
            {!!buttons.length && (
              <div className="flex p-3 items-center justify-between bg-transparent border-t">
                {buttons}
              </div>
            )}
            {props.reassurance.shouldDisplay && (
              <div className="text-gray-light mt-0 border-t-1 mx-2 py-2 text-sm text-left">
                {props.reassurance.text}
              </div>
            )}
          </Fragment>
        )}
      </DropDown>
    </div>
  ) : (
    <Layer
      ariaLabelledby={ID_LAYER_HEADER__SHOPPING__CRITERIA_TRIGGER}
      buttonLeft={{
        iconName: ICONS.LEFT_ARROW,
        label: t('common.backNavigation'),
        onClick: props.onClose,
      }}
      closeLabel={t('common.close')}
      hasAnimateEnter
      header={
        <div
          className="text-center font-normal text-xl text-gray-darker"
          dangerouslySetInnerHTML={{
            __html: criteriaContext.layerHeader,
          }}
          id={ID_LAYER_HEADER__SHOPPING__CRITERIA_TRIGGER}
        />
      }
      onClose={props.onClose}
    >
      <div
        className="scroll-y"
        style={{ height: `calc(100% - ${buttons.length > 0 ? '70px' : '0px'})` }}
      >
        {props.children}
        {props.reassurance.shouldDisplay && (
          <div className="text-gray-light mt-4 lg:mt-0 sm:border-t-1 mx-2 sm:pt-2 text-sm text-left">
            {props.reassurance.text}
          </div>
        )}
      </div>
      {buttons.length && (
        <div className="relative p-3 px-6 lg:px-0 w-full flex items-center justify-between mt-auto bg-white shadow">
          {buttons}
        </div>
      )}
    </Layer>
  );

  return (
    <div ref={rootRef}>
      <InputButton
        className="w-full"
        dataTestId="criteria-trigger-button"
        iconName={props.buttonIcon}
        isActive={props.isOpen}
        isFilled={props.buttonIsFilled}
        isLoading={isDesktop && props.isLoading}
        color={criteriaContext.isJoyview ? COLORS.WATER_GREEN : COLORS.BLUE}
        theme="search"
        onClear={
          isDesktop
            ? () => {
                props.onClear && props.onClear();
              }
            : undefined
        }
        onClick={() => {
          if (props.isOpen) {
            props.onClose && props.onClose();
          } else {
            props.onOpen && props.onOpen();
            if (props.trackingLabel && criteriaContext.trackingActionName) {
              track({
                actionGA: criteriaContext.trackingActionName,
                categoryGA: pageContext.pageId,
                labelGA: props.trackingLabel,
              });
            }
          }
        }}
      >
        {props.buttonLabel}
      </InputButton>
      {props.isOpen && criteriaDropdownOrLayer}
    </div>
  );
};

CriteriaTrigger.defaultProps = {
  buttons: [],
  reassurance: {
    shouldDisplay: false,
    text: '',
  },
};
